/**
 * Client-side form validation utilities
 * Provides validation rules and sanitization functions
 */

// Validation rules
export const validationRules = {
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  
  password: {
    required: true,
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    message: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character'
  },
  
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s'-]+$/,
    message: 'Name must be 2-100 characters and contain only letters, spaces, hyphens, and apostrophes'
  },
  
  workflowName: {
    required: true,
    minLength: 1,
    maxLength: 200,
    pattern: /^[a-zA-Z0-9\s\-_().]+$/,
    message: 'Workflow name must be 1-200 characters and contain only letters, numbers, spaces, and basic punctuation'
  },
  
  businessPlan: {
    required: true,
    minLength: 10,
    maxLength: 1000,
    message: 'Business plan prompt must be between 10 and 1000 characters'
  },
  
  description: {
    required: false,
    maxLength: 1000,
    message: 'Description must be less than 1000 characters'
  },
  
  url: {
    required: false,
    pattern: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
    message: 'Please enter a valid URL'
  },
  
  phone: {
    required: false,
    pattern: /^[\+]?[1-9][\d]{0,15}$/,
    message: 'Please enter a valid phone number'
  }
};

// Sanitization functions
export const sanitize = {
  // Remove HTML tags and dangerous characters
  html: (input) => {
    if (typeof input !== 'string') return input;
    
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/[<>\"'&]/g, (match) => {
        const escapeMap = {
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#x27;',
          '&': '&amp;'
        };
        return escapeMap[match];
      })
      .trim();
  },
  
  // Remove SQL injection patterns
  sql: (input) => {
    if (typeof input !== 'string') return input;
    
    const sqlPatterns = [
      /(\s*(;|--|\||\/\*|\*\/|'|\"|\b(union|select|insert|update|delete|drop|create|alter|exec|execute|sp_|xp_)\b))/gi
    ];
    
    let sanitized = input;
    sqlPatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    return sanitized.trim();
  },
  
  // Remove potentially dangerous characters
  alphanumeric: (input) => {
    if (typeof input !== 'string') return input;
    return input.replace(/[^a-zA-Z0-9\s\-_]/g, '').trim();
  },
  
  // Clean text input
  text: (input) => {
    if (typeof input !== 'string') return input;
    
    return input
      .replace(/[^\w\s\-_.,!?@#$%^&*()+=\[\]{}|;:'"<>]/g, '')
      .trim()
      .substring(0, 1000); // Limit length
  },
  
  // Sanitize email
  email: (input) => {
    if (typeof input !== 'string') return input;
    return input.toLowerCase().trim();
  }
};

// Validation function
export const validate = (value, rules) => {
  const errors = [];
  
  // Required check
  if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    errors.push('This field is required');
    return errors;
  }
  
  // Skip other validations if not required and empty
  if (!rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    return errors;
  }
  
  // Length checks
  if (rules.minLength && value.length < rules.minLength) {
    errors.push(`Must be at least ${rules.minLength} characters long`);
  }
  
  if (rules.maxLength && value.length > rules.maxLength) {
    errors.push(`Must be no more than ${rules.maxLength} characters long`);
  }
  
  // Pattern check
  if (rules.pattern && !rules.pattern.test(value)) {
    errors.push(rules.message || 'Invalid format');
  }
  
  return errors;
};

// Form validation hook
export const useFormValidation = (initialValues, validationSchema) => {
  const [values, setValues] = React.useState(initialValues);
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});
  const [isValid, setIsValid] = React.useState(false);
  
  // Validate single field
  const validateField = (name, value) => {
    const rules = validationSchema[name];
    if (!rules) return [];
    
    const fieldErrors = validate(value, rules);
    return fieldErrors;
  };
  
  // Handle input change
  const handleChange = (name, value) => {
    // Sanitize based on field type
    let sanitizedValue = value;
    if (name === 'email') sanitizedValue = sanitize.email(value);
    else if (name === 'name') sanitizedValue = sanitize.text(value);
    else if (['workflowName', 'description'].includes(name)) sanitizedValue = sanitize.html(value);
    else sanitizedValue = sanitize.text(value);
    
    setValues(prev => ({ ...prev, [name]: sanitizedValue }));
    
    // Validate if field has been touched
    if (touched[name]) {
      const fieldErrors = validateField(name, sanitizedValue);
      setErrors(prev => ({ ...prev, [name]: fieldErrors }));
    }
  };
  
  // Handle field blur
  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const fieldErrors = validateField(name, values[name]);
    setErrors(prev => ({ ...prev, [name]: fieldErrors }));
  };
  
  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    let formIsValid = true;
    
    Object.keys(validationSchema).forEach(field => {
      const fieldErrors = validateField(field, values[field]);
      if (fieldErrors.length > 0) {
        newErrors[field] = fieldErrors;
        formIsValid = false;
      }
    });
    
    setErrors(newErrors);
    setIsValid(formIsValid);
    return formIsValid;
  };
  
  // Reset form
  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsValid(false);
  };
  
  React.useEffect(() => {
    const newIsValid = Object.keys(validationSchema).every(field => {
      const fieldErrors = validateField(field, values[field]);
      return fieldErrors.length === 0;
    });
    setIsValid(newIsValid);
  }, [values]);
  
  return {
    values,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    validateForm,
    reset
  };
};

// Common validation schemas
export const schemas = {
  login: {
    email: validationRules.email
  },
  
  businessPlan: {
    prompt: validationRules.businessPlan
  },
  
  workflow: {
    name: validationRules.workflowName,
    description: validationRules.description
  },
  
  invitation: {
    email: validationRules.email
  },
  
  contact: {
    name: validationRules.name,
    email: validationRules.email,
    message: {
      required: true,
      minLength: 10,
      maxLength: 500,
      message: 'Message must be between 10 and 500 characters'
    }
  }
};

export default {
  validationRules,
  sanitize,
  validate,
  useFormValidation,
  schemas
};
